/** The hovered path and the target set: a classic signature.
 *
 * Without it the map is a picture. With it the player can read, at a glance,
 * where the route goes, where tomorrow begins, and what happens at the end of
 * it - which is most of what adventure-map play *is*.
 *
 * ## There is no reachable wash
 *
 * There used to be: every cell the selected hero could enter took a flood fill
 * of pale green. On a fresh hero with 1800 movement that is fifteen tiles in
 * every direction, and it did not cover part of the viewport, it covered most
 * of it. Measured by rendering the adventure scene twice, once with the fill
 * and once without, and differencing: 1,160,768 pixels of the 1620x1010 map
 * area, 70.9% of it, in one rectilinear slab whose edge steps in 64px treads -
 * the tile pitch, showing the grid the terrain works hard not to show.
 *
 * The classic draws nothing at all on the ground for a hero's reach. The movement
 * budget is a number and a bar in the sidebar; the affordance on the map is the
 * chain of arrows to wherever the cursor is, with a marker on the last cell
 * saying what happens there. That is what `drawPath` does, and it is enough.
 *
 * The per-cell marks survive for the case they are actually good at: a SMALL,
 * deliberately chosen set of cells - a hero with a few points left, or the
 * legal targets of an armed adventure spell (`apps/web/src/world/aiming.ts`
 * hands its target set to this same layer). Past `MARK_CELL_LIMIT` cells the
 * layer draws nothing but the selection ring, because a mark on six hundred
 * cells is not an affordance, it is confetti.
 *
 * The renderer decides none of it (I10). `ReachableSet` comes from
 * `computeReachable` in the kernel and the path comes from `pathTo`; this file
 * only chooses colours and draws them. It never asks whether a step is legal.
 *
 * Drawing is retained: the overlay is rebuilt when the reach or the hovered
 * path changes, not every frame, because a `Graphics` rebuild over a few
 * hundred cells is far more expensive than re-submitting one that already
 * exists.
 */

import { Container, Graphics } from "pixi.js";
import type { CellRect } from "../coords.js";
import { TILE_SIZE } from "../coords.js";

/** `UNREACHABLE_COST` from `core/src/map/path.ts`, duplicated rather than
 *  imported: `packages/render` may not reach into a rule module (I10). */
export const UNREACHABLE_COST = 0x7fffffff;

export interface ReachableLike {
  readonly origin: number;
  readonly cost: Int32Array;
  readonly reachable: Uint8Array;
}

export type CursorKind =
  | "move" | "attack" | "visit" | "enter" | "pickup" | "boat" | "blocked";

export interface PathStep {
  readonly x: number;
  readonly y: number;
  /** Movement points spent to stand here; the split between today and tomorrow. */
  readonly cost: number;
}

export interface HoverPath {
  readonly steps: readonly PathStep[];
  /** Movement the hero has left today; steps past it are drawn as tomorrow's. */
  readonly budget: number;
  readonly cursor: CursorKind;
}

/** The per-cell mark. Muted, not mint: a saturated pale green diamond on grass
 *  is the second brightest thing on the map and out-shouts the hero, which is
 *  precisely backwards. */
const TODAY_MARK = 0x9fd8a6;
/** The second-tier mark differs from the first in VALUE first and hue second: a
 *  hue step alone does not survive being scattered over grass, sand and cobble.
 *  It is only ever drawn when `nextTurnBudget` is set, which by default it is
 *  not - see `HighlightOptions`. */
const TOMORROW_MARK = 0x7a5a1c;
const BLOCKED = 0xd6483a;
/** Every mark and every region edge is seated on this before its own colour. */
const BACKING = 0x0b0f14;

/** Half-diagonal of a reach mark, in world pixels. A sixth of a tile reads as a
 *  tick on the cell rather than as a pebble lying on it. */
const MARK_RADIUS = 5;

/**
 * Above this many marked cells on screen the ground overlay is dropped whole.
 *
 * 200 rather than 120, and the number is not free: Blink Step at expert
 * offers a Chebyshev range of five, which is an 11x11 block - 120 cells of it
 * once the caster's own cell is taken out, and 120 was exactly the old cut, so
 * the one spell that most needs its legal set drawn was on the wrong side of
 * the threshold about as often as not. 200 clears that with room to spare and
 * is still far under the ~600 cells a fresh hero's walk reach covers, which is
 * the case that must draw nothing.
 */
const MARK_CELL_LIMIT = 200;

/** The selected hero's under-foot marker, in world pixels. Wide enough to seat
 *  a horse's four hooves, not just the near pair. */
const SELECT_RX = 25;
const SELECT_RY = 10;

/**
 * The route arrow.
 *
 * Colours and proportions were said to be taken off
 * A reference screenshot, which was said to
 * have a boat route running across open water with both tiers of arrow in one
 * frame: arrow bodies (16,150,24) green and (156,20,0) red - flat, saturated, no
 * gradient - inside a light grey keyline running (156,158,156) with a
 * (206,207,206) highlight.
 *
 * **UNVERIFIABLE.** That file does not exist; no `refs/adventure/` directory has
 * ever been on this machine. Checked 2026-08-30 against the five genuine
 * adventure frames that ARE on disk (a reference screenshot, `classic8`,
 * `classic12`, `classic19`, `classic20`): none of them has a movement path plotted, so none
 * shows a route arrow. Scanned for the arrow's own colours - green with G > 110
 * and R,B < 70, red with R > 120 and G < 60, B < 50 - the four map viewports
 * return 0 to 1923 scattered pixels each, all of them vegetation, crystal and
 * roof tile, and not one contiguous arrow anywhere.
 *
 * So the six triples below are remembered, not checkable. They stay because
 * they are what this layer has shipped with and because the GREEN/RED pairing
 * is a rule any the classic player can state without a screenshot; the exact channel
 * values are not evidence of anything.
 *
 * Green is THIS TURN and red is NOT THIS TURN. That pairing is the one every
 * The classic player already knows, and the amber this file used before said neither
 * thing; it read as a third state that does not exist.
 *
 * The keyline being LIGHT is the part that is easy to get wrong. A dark outline
 * is the obvious way to make a mark survive any terrain, and it is what this
 * file did; but on the dark blue water of the reference a dark outline
 * disappears and the arrow loses its shape, while a light one holds on water,
 * grass, cobble and lava alike.
 */
const TODAY_ARROW = 0x109618;
const LATER_ARROW = 0x9c1400;
const ARROW_KEYLINE = 0x9c9e9c;
const ARROW_HIGHLIGHT = 0xcecfce;

/**
 * The arrow, in world pixels, as a block arrow rather than a dart.
 *
 * Measured on the reference at 800x600, where a tile is 32px: the arrow is 26px
 * long (0.81 of a tile), its head is 20px across (0.63) and its shaft 9px
 * (0.28). Scaled to this build's 64px tile that is 52 / 40 / 18, and the run of
 * arrows in the reference does very nearly touch corner to corner - they are
 * meant to read as one continuous route, not as separate ticks.
 */
const ARROW_LENGTH = 50;
const ARROW_HEAD_LENGTH = 22;
const ARROW_HEAD_HALF = 20;
const ARROW_SHAFT_HALF = 9;

export interface HighlightOptions {
  /**
   * Cells with a finite cost beyond `budget` are tomorrow's, up to this much more.
   *
   * Zero by default, and that is deliberate: the classic marks only the tiles reachable
   * THIS turn on the map, and shows the multi-day continuation solely as the
   * arrow chain under the cursor. A second tier drawn across the ground is a
   * later-series idea, and at a real movement budget it covers 30-odd tiles in
   * every direction - which is to say the whole visible map, at which point the
   * overlay stops being information and becomes a lattice of confetti over the
   * terrain. Set it only if something genuinely wants the two-day wash.
   */
  readonly nextTurnBudget?: number;
}

export class HighlightLayer {
  /** Ground tint; belongs under objects so a castle is not painted green. */
  readonly ground = new Container({ label: "reach" });
  /** Arrows and the cursor; belong over objects so the route is never hidden. */
  readonly overlay = new Container({ label: "path" });

  private readonly reachGfx = new Graphics();
  private readonly pathGfx = new Graphics();
  private reach: ReachableLike | null = null;
  private path: HoverPath | null = null;
  /** `ObservedMap.visibility`, so the overlay stops at the fog rather than
   *  being painted into it. Null before the first observation. */
  private visibility: Uint8Array | null = null;
  private selectColour = 0xd9b25a;
  private budget = 0;
  private rect: CellRect = { x0: 0, y0: 0, x1: 0, y1: 0 };
  private mapW = 0;
  private mapH = 0;
  private z = 0;
  private reachDirty = false;
  private pathDirty = false;
  private readonly nextTurnBudget: number;

  constructor(opts: HighlightOptions = {}) {
    this.ground.addChild(this.reachGfx);
    this.overlay.addChild(this.pathGfx);
    this.nextTurnBudget = opts.nextTurnBudget ?? 0;
  }

  setMap(width: number, height: number, z: number): void {
    if (this.mapW === width && this.mapH === height && this.z === z) return;
    this.mapW = width;
    this.mapH = height;
    this.z = z;
    this.reachDirty = true;
  }

  setReach(reach: ReachableLike | null, budget: number): void {
    this.reach = reach;
    this.budget = budget;
    this.reachDirty = true;
  }

  /**
   * The fog plane, straight from the observation.
   *
   * The kernel's reach set is computed on the real map, so it happily contains
   * cells the viewer has never seen. Drawing a mark there paints an overlay
   * into the black, which both leaks the shape of the unexplored map and reads
   * as a rendering fault. The overlay is clipped to what the player can see.
   */
  setVisibility(plane: Uint8Array | null): void {
    this.visibility = plane;
    this.reachDirty = true;
  }

  /** Owner colour of the selected hero, for the under-foot marker. */
  setSelectionColour(colour: number): void {
    if (this.selectColour === colour) return;
    this.selectColour = colour;
    this.reachDirty = true;
  }

  setPath(path: HoverPath | null): void {
    this.path = path;
    this.pathDirty = true;
  }

  setVisible(reach: boolean, path: boolean): void {
    this.ground.visible = reach;
    this.overlay.visible = path;
  }

  /** Redraw whatever changed. `rect` is the visible cell range. */
  update(rect: CellRect): void {
    const moved = rect.x0 !== this.rect.x0 || rect.y0 !== this.rect.y0
      || rect.x1 !== this.rect.x1 || rect.y1 !== this.rect.y1;
    this.rect = rect;
    if (this.reachDirty || moved) {
      this.drawReach();
      this.reachDirty = false;
    }
    if (this.pathDirty) {
      this.drawPath();
      this.pathDirty = false;
    }
  }

  // ------------------------------------------------------------ reach

  private drawReach(): void {
    const g = this.reachGfx;
    g.clear();
    const r = this.reach;
    if (r === null || this.mapW === 0) return;

    const plane = this.mapW * this.mapH * this.z;
    const vis = this.visibility;
    const classOf = (x: number, y: number): 0 | 1 | 2 => {
      if (x < 0 || y < 0 || x >= this.mapW || y >= this.mapH) return 0;
      const i = plane + y * this.mapW + x;
      // Clipped to what the viewer can see: the reach set is computed on the
      // real map and runs straight on under the fog.
      if (vis !== null && (vis[i] ?? 0) === 0) return 0;
      if ((r.reachable[i] ?? 0) === 1) return 1;
      const c = r.cost[i] ?? UNREACHABLE_COST;
      if (c >= UNREACHABLE_COST) return 0;
      return c <= this.budget + this.nextTurnBudget ? 2 : 0;
    };

    // ---- one mark per marked cell, and nothing else
    //
    // There is no fill pass. Two of them have been tried and both failed the
    // same way: at 0.28 with a border the reach read as a grey debug box, and
    // at 0.11 without one it read as a pale rectilinear slab of changed light
    // over a third of the map. Neither is anything the classic draws. What the classic
    // draws on the ground is nothing; what it draws under the cursor is the
    // arrow chain in `drawPath`.
    //
    // The classic marks the reach per tile, and that is not decoration: the player is
    // counting cells, so the overlay has to be countable. A diamond rather than
    // a square because a square at cell pitch rebuilds the grid lines the
    // terrain works so hard not to show.
    //
    // Each mark is drawn twice, dark then bright, because a single coloured
    // pass disappears into half the terrain set - and the dark pass is what
    // keeps a pale mark from vanishing on light cobble.
    const origin = r.origin - plane;
    const ox = origin % this.mapW;
    const oy = Math.floor(origin / this.mapW);
    const marks = (want: 1 | 2, radius: number): boolean => {
      let any = false;
      for (let y = this.rect.y0; y < this.rect.y1; y++) {
        for (let x = this.rect.x0; x < this.rect.x1; x++) {
          if (classOf(x, y) !== want) continue;
          // The hero's own cell carries the selection marker instead.
          if (x === ox && y === oy) continue;
          const cx = x * TILE_SIZE + TILE_SIZE / 2;
          const cy = y * TILE_SIZE + TILE_SIZE / 2;
          g.moveTo(cx, cy - radius);
          g.lineTo(cx + radius, cy);
          g.lineTo(cx, cy + radius);
          g.lineTo(cx - radius, cy);
          g.closePath();
          any = true;
        }
      }
      return any;
    };

    const countClass = (want: 1 | 2): number => {
      let n = 0;
      for (let y = this.rect.y0; y < this.rect.y1; y++) {
        for (let x = this.rect.x0; x < this.rect.x1; x++) if (classOf(x, y) === want) n++;
      }
      return n;
    };

    // The marks are drawn only when the set is SMALL enough to be worth
    // counting.
    //
    // The idea was that a player counts tiles, so the overlay should be
    // countable. That holds for a hero with a few points left and for an armed
    // spell's target list. It fails badly for a fresh hero: 1500 movement is
    // fifteen tiles in every direction, which at this tile pitch is the entire
    // viewport, and a mark on every one of ~600 cells stops being an affordance
    // and becomes a lattice of confetti lying on the terrain - the exact
    // regular grid the dual-grid terrain work exists to avoid. Past this many
    // cells NOTHING is drawn on the ground, and the arrow chain under the
    // cursor carries the route, which is how the original conveys it.
    if (countClass(1) + countClass(2) <= MARK_CELL_LIMIT) {
      for (const want of [1, 2] as const) {
        if (marks(want, MARK_RADIUS + 1.5)) {
          g.fill({ color: BACKING, alpha: 0.55 });
        }
        if (marks(want, MARK_RADIUS)) {
          g.fill({
            color: want === 1 ? TODAY_MARK : TOMORROW_MARK,
            alpha: want === 1 ? 0.72 : 0.8,
          });
        }
      }
    }

    this.drawSelection(ox, oy, origin >= 0 && origin < this.mapW * this.mapH);
  }

  /**
   * The selected hero's under-foot marker.
   *
   * The hero is one tile of sprite standing in front of a six-tile castle whose
   * banners are the same red as his own. Nothing in the frame said which figure
   * the sidebar was talking about, so a ring is drawn on the ground he is
   * standing on, in his owner's colour, seated on a dark ellipse so it survives
   * cobble as well as grass. It is under the sprite, so it never crosses him.
   *
   * The reach set's `origin` is the hero's cell by construction - it is the
   * cell the kernel ran Dijkstra from - so the marker needs no selection API of
   * its own and can never disagree with the overlay around it.
   */
  private drawSelection(x: number, y: number, ok: boolean): void {
    if (!ok) return;
    const g = this.reachGfx;
    const cx = x * TILE_SIZE + TILE_SIZE / 2;
    // The GROUND LINE, not a fraction of the cell. A sprite standing on this
    // cell has its feet on `(y + 1) * TILE`, so a marker drawn at 0.86 of the
    // cell sat nine pixels up the legs and ringed a horse's hocks instead of
    // seating under the whole animal. Two pixels of lift is the contact
    // shadow's own thickness and nothing more.
    const cy = (y + 1) * TILE_SIZE - 2;

    // Four passes, dark to light, so it reads as something worn into the
    // ground rather than as a vector annotation: a soft pool, a seated dark
    // groove, the owner's colour, and a lit lip on the near side of the ring.
    g.ellipse(cx, cy, SELECT_RX + 5, SELECT_RY + 3)
      .fill({ color: BACKING, alpha: 0.18 });
    g.ellipse(cx, cy, SELECT_RX + 2, SELECT_RY + 2)
      .stroke({ width: 5, color: BACKING, alpha: 0.5 });
    g.ellipse(cx, cy, SELECT_RX, SELECT_RY)
      .stroke({ width: 3, color: this.selectColour, alpha: 0.95 });
    g.ellipse(cx, cy + 1, SELECT_RX - 1, SELECT_RY - 1)
      .stroke({ width: 1, color: 0xffffff, alpha: 0.28 });
    g.ellipse(cx, cy, SELECT_RX - 3.5, SELECT_RY - 2)
      .stroke({ width: 1, color: 0x000000, alpha: 0.35 });
  }

  // ------------------------------------------------------------ path

  private drawPath(): void {
    const g = this.pathGfx;
    g.clear();
    const p = this.path;
    if (p === null || p.steps.length === 0) return;

    const centre = (s: PathStep): { x: number; y: number } =>
      ({ x: s.x * TILE_SIZE + TILE_SIZE / 2, y: s.y * TILE_SIZE + TILE_SIZE / 2 });

    // One arrow per cell stepped onto, pointing the way the hero will leave it:
    // The classic's route marker, and the reason a player can read a six-cell detour
    // around a monster without tracing it. Three passes per tier - a highlight,
    // the keyline, then the flat body - which is how the reference art is built
    // and what makes one shape hold on water, grass and cobble alike.
    const arrows = (today: boolean, grow: number): boolean => {
      let any = false;
      for (let i = 1; i < p.steps.length; i++) {
        const step = p.steps[i]!;
        if ((step.cost <= p.budget) !== today) continue;
        blockArrow(g, centre(p.steps[i - 1]!), centre(step), grow);
        any = true;
      }
      return any;
    };

    for (const today of [true, false]) {
      if (arrows(today, 3.4)) g.fill({ color: ARROW_HIGHLIGHT, alpha: 0.85 });
      if (arrows(today, 1.8)) g.fill({ color: ARROW_KEYLINE, alpha: 1 });
      if (arrows(today, 0)) {
        g.fill({ color: today ? TODAY_ARROW : LATER_ARROW, alpha: 1 });
      }
    }

    const last = p.steps[p.steps.length - 1]!;
    drawCursor(g, centre(last), p.cursor, last.cost <= p.budget);
  }

  destroy(): void {
    this.ground.destroy({ children: true, context: true });
    this.overlay.destroy({ children: true, context: true });
  }
}

/**
 * A block arrow centred on `b`, pointing away from `a`, grown by `grow` on
 * every edge so the same routine draws the highlight, the keyline and the body.
 *
 * A block arrow - shaft plus head - rather than a plain dart, because that is
 * the silhouette in the reference and it is a better one: the shaft gives the
 * arrow a direction you can read at a glance from across the map, where a
 * triangle only tells you where its point is. Solid rather than stroked: a 4px
 * stroke over grass is competing with grass blades of the same width and the
 * arrow loses.
 */
function blockArrow(
  g: Graphics, a: { x: number; y: number }, b: { x: number; y: number }, grow: number,
): void {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const nx = -uy;
  const ny = ux;
  const half = ARROW_LENGTH / 2 + grow;
  const head = ARROW_HEAD_LENGTH + grow;
  const wing = ARROW_HEAD_HALF + grow;
  const shaft = ARROW_SHAFT_HALF + grow;
  // Distance from the centre back to where the head meets the shaft.
  const neck = half - head;
  const at = (along: number, across: number): [number, number] =>
    [b.x + ux * along + nx * across, b.y + uy * along + ny * across];

  const p = [
    at(half, 0),
    at(neck, -wing),
    at(neck, -shaft),
    at(-half, -shaft),
    at(-half, shaft),
    at(neck, shaft),
    at(neck, wing),
  ];
  g.moveTo(p[0]![0], p[0]![1]);
  for (let i = 1; i < p.length; i++) g.lineTo(p[i]![0], p[i]![1]);
  g.closePath();
}


/**
 * What happens when the hero arrives, said in shapes rather than words. The
 * text version is a React tooltip (D8); this is the part that has to be
 * anchored in the world and scale with the camera.
 *
 * The classic puts a contextual glyph on the destination cell - an anchor for a boat,
 * a red cross for a cell that cannot be entered - in the same green-or-red as
 * the route that leads to it, so the end of the chain says both WHAT happens
 * and WHETHER it happens today. The glyph is seated on the same light keyline
 * the arrows carry, so the whole route is one visual object.
 */
function drawCursor(
  g: Graphics, c: { x: number; y: number }, kind: CursorKind, today: boolean,
): void {
  const colour = kind === "blocked" ? BLOCKED : today ? TODAY_ARROW : LATER_ARROW;
  const r = 21;
  g.circle(c.x, c.y, r + 4).fill({ color: ARROW_KEYLINE, alpha: 0.92 });
  g.circle(c.x, c.y, r + 1).fill({ color: 0x121821, alpha: 0.72 });
  g.circle(c.x, c.y, r).stroke({ width: 4, color: colour, alpha: 1 });

  switch (kind) {
    case "attack":
      g.moveTo(c.x - 8, c.y - 8); g.lineTo(c.x + 8, c.y + 8);
      g.moveTo(c.x + 8, c.y - 8); g.lineTo(c.x - 8, c.y + 8);
      g.stroke({ width: 3.5, color: colour, cap: "round" });
      break;
    case "blocked":
      g.moveTo(c.x - 9, c.y); g.lineTo(c.x + 9, c.y);
      g.stroke({ width: 4, color: colour, cap: "round" });
      break;
    case "visit":
    case "pickup":
      g.moveTo(c.x, c.y - 9); g.lineTo(c.x + 9, c.y);
      g.lineTo(c.x, c.y + 9); g.lineTo(c.x - 9, c.y);
      g.closePath();
      g.stroke({ width: 3, color: colour, join: "round" });
      break;
    case "enter":
    case "boat":
      g.moveTo(c.x - 8, c.y + 6); g.lineTo(c.x, c.y - 8); g.lineTo(c.x + 8, c.y + 6);
      g.stroke({ width: 3, color: colour, cap: "round", join: "round" });
      break;
    case "move":
    default:
      g.circle(c.x, c.y, 4).fill({ color: colour, alpha: 0.95 });
      break;
  }
}
